import { createApiClient, getAuthHeaders } from "@/lib/api-client";

import type {
  ApiSuccessResponse,
  AuditEntry,
  CreateFlagInput,
  FeatureFlag,
  FlagWithRules,
  TargetingRule,
  UpdateFlagInput,
} from "./feature-flags-types";

export interface RuleInput {
  readonly ruleId?: string;
  readonly variationKey: string;
  readonly variationValue: unknown;
  readonly rolloutPct?: number;
  readonly priority?: number;
}

const featureFlagsApi = createApiClient({
  baseUrl: import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/feature-flags`
    : "http://localhost:3002",
  getAuthHeaders,
});

// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------

export async function fetchFlags(): Promise<FeatureFlag[]> {
  const response = await featureFlagsApi.get<ApiSuccessResponse<FeatureFlag[]>>("/flags");
  return response.data;
}

export async function fetchFlagWithRules(key: string): Promise<FlagWithRules> {
  const response = await featureFlagsApi.get<ApiSuccessResponse<FlagWithRules>>(
    `/flags/${encodeURIComponent(key)}`
  );
  return response.data;
}

export async function createFlag(input: CreateFlagInput): Promise<FeatureFlag> {
  const response = await featureFlagsApi.post<ApiSuccessResponse<FeatureFlag>>("/flags", input);
  return response.data;
}

export async function updateFlag(key: string, input: UpdateFlagInput): Promise<FeatureFlag> {
  const response = await featureFlagsApi.put<ApiSuccessResponse<FeatureFlag>>(
    `/flags/${encodeURIComponent(key)}`,
    input
  );
  return response.data;
}

export async function deleteFlag(key: string): Promise<void> {
  await featureFlagsApi.del(`/flags/${encodeURIComponent(key)}`);
}

export async function toggleFlag(key: string, enabled: boolean): Promise<FeatureFlag> {
  return updateFlag(key, { enabled });
}

// ---------------------------------------------------------------------------
// Targeting rules
//
// A flag with no rule serves its default variation in every environment, so
// these are what make a per-environment toggle mean anything.
// ---------------------------------------------------------------------------

export async function createRule(key: string, input: RuleInput): Promise<TargetingRule> {
  const response = await featureFlagsApi.post<ApiSuccessResponse<TargetingRule>>(
    `/flags/${encodeURIComponent(key)}/rules`,
    input
  );
  return response.data;
}

export async function updateRule(
  key: string,
  ruleId: string,
  input: Omit<RuleInput, "ruleId">
): Promise<TargetingRule> {
  const response = await featureFlagsApi.put<ApiSuccessResponse<TargetingRule>>(
    `/flags/${encodeURIComponent(key)}/rules/${encodeURIComponent(ruleId)}`,
    input
  );
  return response.data;
}

export async function deleteRule(key: string, ruleId: string): Promise<void> {
  await featureFlagsApi.del(
    `/flags/${encodeURIComponent(key)}/rules/${encodeURIComponent(ruleId)}`
  );
}

/**
 * Toggle a flag for a single environment. Reads the current per-env map,
 * patches just the target env, and PUTs the merged map back so other envs
 * stay untouched.
 */
export async function toggleFlagEnvironment(
  flag: FeatureFlag,
  environment: string,
  enabled: boolean
): Promise<FeatureFlag> {
  const next = { ...(flag.enabledByEnvironment ?? {}), [environment]: enabled };
  return updateFlag(flag.flagKey, { enabledByEnvironment: next });
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export async function fetchAuditLog(key: string): Promise<AuditEntry[]> {
  const response = await featureFlagsApi.get<ApiSuccessResponse<AuditEntry[]>>(
    `/flags/${encodeURIComponent(key)}/audit`
  );
  return response.data;
}
